import { createContext, useContext } from 'react';
import { en, type MessageKey } from './en.ts';
import { tr } from './tr.ts';

type Messages = Record<MessageKey, string>;

export interface LocaleEntry {
  code: string;
  /** Name in its own language. */
  label: string;
  /** Intl locale for numbers and dates. */
  intl: string;
  dir: 'ltr' | 'rtl';
  /** Only READY locales (complete, reviewed catalogue) are offered to players. */
  status: 'READY' | 'PLANNED';
  messages: Messages | null;
}

/**
 * Locale catalogue. The infrastructure takes any number of locales; a locale is listed for players only
 * when its message catalogue is complete. PLANNED entries fix intl/dir so formatting and layout can be
 * tested before translation.
 */
export const LOCALE_CATALOGUE: readonly LocaleEntry[] = [
  { code: 'en', label: 'English', intl: 'en-US', dir: 'ltr', status: 'READY', messages: en as Messages },
  { code: 'tr', label: 'Türkçe', intl: 'tr-TR', dir: 'ltr', status: 'READY', messages: tr },
  { code: 'de', label: 'Deutsch', intl: 'de-DE', dir: 'ltr', status: 'PLANNED', messages: null },
  { code: 'fr', label: 'Français', intl: 'fr-FR', dir: 'ltr', status: 'PLANNED', messages: null },
  { code: 'es', label: 'Español', intl: 'es-ES', dir: 'ltr', status: 'PLANNED', messages: null },
  { code: 'pt-BR', label: 'Português (Brasil)', intl: 'pt-BR', dir: 'ltr', status: 'PLANNED', messages: null },
  { code: 'it', label: 'Italiano', intl: 'it-IT', dir: 'ltr', status: 'PLANNED', messages: null },
  { code: 'nl', label: 'Nederlands', intl: 'nl-NL', dir: 'ltr', status: 'PLANNED', messages: null },
  { code: 'pl', label: 'Polski', intl: 'pl-PL', dir: 'ltr', status: 'PLANNED', messages: null },
  { code: 'ru', label: 'Русский', intl: 'ru-RU', dir: 'ltr', status: 'PLANNED', messages: null },
  { code: 'uk', label: 'Українська', intl: 'uk-UA', dir: 'ltr', status: 'PLANNED', messages: null },
  { code: 'ja', label: '日本語', intl: 'ja-JP', dir: 'ltr', status: 'PLANNED', messages: null },
  { code: 'ko', label: '한국어', intl: 'ko-KR', dir: 'ltr', status: 'PLANNED', messages: null },
  { code: 'zh-Hans', label: '简体中文', intl: 'zh-CN', dir: 'ltr', status: 'PLANNED', messages: null },
  { code: 'hi', label: 'हिन्दी', intl: 'hi-IN', dir: 'ltr', status: 'PLANNED', messages: null },
  { code: 'id', label: 'Bahasa Indonesia', intl: 'id-ID', dir: 'ltr', status: 'PLANNED', messages: null },
  { code: 'vi', label: 'Tiếng Việt', intl: 'vi-VN', dir: 'ltr', status: 'PLANNED', messages: null },
  { code: 'ar', label: 'العربية', intl: 'ar-EG', dir: 'rtl', status: 'PLANNED', messages: null },
  { code: 'he', label: 'עברית', intl: 'he-IL', dir: 'rtl', status: 'PLANNED', messages: null },
];

/** What players can choose. */
export const LOCALES = LOCALE_CATALOGUE.filter((l): l is LocaleEntry & { messages: Messages } => l.status === 'READY' && l.messages !== null);

/** 'pseudo' and 'rtl' are dev-only fixtures (long strings; right-to-left layout with he-IL numbers). */
export type LocaleCode = 'en' | 'tr' | 'pseudo' | 'rtl';

/** Dev-only pseudo locale: ~40% longer strings with accents, to catch overflow (like long DE/TR text). */
function pseudo(s: string): string {
  const map: Record<string, string> = { a: 'ä', e: 'ë', i: 'ï', o: 'ö', u: 'ü', A: 'Ä', E: 'Ë', O: 'Ö', U: 'Ü', c: 'ç', s: 'š' };
  const body = s.replace(/\{[^}]+\}|./g, (m) => (m.length > 1 ? m : (map[m] ?? m)));
  return `⟦${body}${' ~'.repeat(Math.ceil(s.length * 0.2))}⟧`;
}

export interface I18n {
  locale: LocaleCode;
  intl: string;
  dir: 'ltr' | 'rtl';
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
}

export function makeI18n(locale: LocaleCode): I18n {
  const entry = LOCALES.find((l) => l.code === locale) ?? LOCALES[0]!;
  const t = (key: MessageKey, vars?: Record<string, string | number>) => {
    const raw: string | undefined = locale === 'pseudo' || locale === 'rtl' ? en[key] : entry.messages[key];
    if (raw === undefined) return key;
    let s = locale === 'pseudo' ? pseudo(raw) : raw;
    if (vars) for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{${k}}`, String(v));
    return s;
  };
  const intl = locale === 'pseudo' ? 'de-DE' : locale === 'rtl' ? 'he-IL' : entry.intl;
  return { locale, intl, dir: locale === 'rtl' ? 'rtl' : entry.dir, t };
}

export const I18nContext = createContext<I18n>(makeI18n('en'));
export const useI18n = () => useContext(I18nContext);
export type { MessageKey };
